import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Navigation, Phone, PenLine, CheckCircle2, Loader2, MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import SignaturePad from "../components/SignaturePad";
import PodPhotos from "../components/PodPhotos";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { subscribeJob, subscribeStopDoc, completeDelivery, mapsUrl, stopPhotos, STOP_STATUS_META } from "../lib/deliveries";

const STATUS_CHOICES = [
  { key: "delivered", label: "Delivered" },
  { key: "attempted", label: "Attempted" },
  { key: "failed", label: "Failed" }
];

export default function DriverStop() {
  const { jobId, stopId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [stop, setStop] = useState(null);
  const [status, setStatus] = useState("delivered");
  const [recipient, setRecipient] = useState("");
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState(null);
  const [showSig, setShowSig] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { const u1 = subscribeJob(jobId, setJob); const u2 = subscribeStopDoc(stopId, setStop); return () => { u1(); u2(); }; }, [jobId, stopId]);

  useEffect(() => { if (stop) { setRecipient(stop.recipient_received_by || ""); setNotes(stop.driver_notes || ""); } }, [stop?.id]); // eslint-disable-line

  if (!job || !stop) return <div className="p-16 text-center text-slate-500">Loading stop…</div>;

  const hasPhoto = stopPhotos(stop).length > 0;
  const canComplete = status !== "delivered" || (hasPhoto && recipient.trim());

  const complete = async () => {
    if (status === "delivered" && !hasPhoto) { toast({ title: "Photo required", description: "Add a POD photo to confirm delivery." }); return; }
    if (status === "delivered" && !recipient.trim()) { toast({ title: "Recipient required", description: "Enter who received the package." }); return; }
    setSaving(true);
    let coords = null;
    try {
      coords = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          (p) => res({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
          () => res(null),
          { timeout: 6000 }
        );
      });
    } catch { coords = null; }
    try {
      await completeDelivery({ job, stop, status, recipientName: recipient, notes, signatureDataUrl: signature, driver: user, coords });
      toast({ title: `Marked ${status}`, description: `Stop ${stop.stop_number} · POD saved.` });
      navigate(`/driver/deliveries/${jobId}`);
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.message || "Check connection / Firebase Storage." });
    } finally { setSaving(false); }
  };

  const m = STOP_STATUS_META[stop.status] || STOP_STATUS_META.pending;

  return (
    <div className="bg-slate-50 min-h-screen pb-28">
      <section className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-5 max-w-xl">
          <Link to={`/driver/deliveries/${jobId}`} className="inline-flex items-center gap-2 text-xs text-slate-300 mb-2"><ArrowLeft className="w-4 h-4" /> Stops</Link>
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#f59321]">{job.job_reference}</div>
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>
          </div>
          <div className="mt-1 font-display text-lg font-extrabold">STOP {String(stop.stop_number).padStart(2, "0")} / {job.total_stops}</div>
        </div>
      </section>

      <section className="container-518 py-5 max-w-xl space-y-4" data-testid="driver-stop-workflow">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="font-display text-2xl font-extrabold text-[#0f2847]">{stop.recipient_name}</div>
          {stop.company_name && <div className="text-slate-600 font-semibold">{stop.company_name}</div>}
          <div className="mt-1 text-slate-600 flex items-start gap-2"><MapPin className="w-4 h-4 mt-1 text-[#f59321] shrink-0" /><span>{stop.address_line_1}{stop.address_line_2 ? `, ${stop.address_line_2}` : ""}<br />{stop.city}, {stop.state} {stop.zip_code}</span></div>
          <div className="mt-2 text-xs font-bold text-[#0f2847]">Package: {stop.package_reference}</div>
          {stop.delivery_instructions && <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">{stop.delivery_instructions}</div>}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <a href={mapsUrl(stop)} target="_blank" rel="noreferrer" data-testid="stop-navigate">
              <Button className="w-full bg-[#0f2847] text-white rounded-xl h-14 text-base font-bold"><Navigation className="w-5 h-5 mr-2" /> Navigate</Button>
            </a>
            <a href={stop.phone ? `tel:${stop.phone.replace(/[^0-9]/g, "")}` : "tel:5189544701"} data-testid="stop-call">
              <Button variant="outline" className="w-full rounded-xl h-14 text-base font-bold border-2"><Phone className="w-5 h-5 mr-2" /> Call</Button>
            </a>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-sm font-bold text-[#0f2847] mb-2">Status</div>
          <div className="grid grid-cols-3 gap-2">
            {STATUS_CHOICES.map((c) => (
              <button key={c.key} onClick={() => setStatus(c.key)} data-testid={`status-${c.key}`}
                className={`h-12 rounded-xl font-bold text-sm border-2 transition ${status === c.key ? "bg-[#f59321] border-[#f59321] text-white" : "bg-white border-slate-200 text-slate-600"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Photo */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-sm font-bold text-[#0f2847] mb-2">Proof of delivery photo {status === "delivered" && <span className="text-red-500">*</span>}</div>
          <p className="text-xs text-slate-500 mb-3">Take a new photo or upload existing ones from your device. Multiple photos allowed.</p>
          <PodPhotos job={job} stop={stop} user={user} canEdit />
        </div>

        {/* Recipient */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <label className="text-sm font-bold text-[#0f2847]">Received by {status === "delivered" && <span className="text-red-500">*</span>}</label>
          <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Recipient name" className="h-12 mt-2 rounded-xl border-slate-200 text-base" data-testid="stop-recipient" />
        </div>

        {/* Signature */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold text-[#0f2847]">Signature <span className="text-slate-400 font-normal">(optional)</span></div>
            {signature && <span className="text-xs font-bold text-emerald-600">Captured ✓</span>}
          </div>
          {showSig ? (
            <SignaturePad onSave={(d) => { setSignature(d); setShowSig(false); toast({ title: "Signature saved" }); }} onClear={() => setSignature(null)} />
          ) : (
            <Button onClick={() => setShowSig(true)} variant="outline" className="w-full rounded-xl h-12 font-semibold border-2" data-testid="stop-add-signature"><PenLine className="w-4 h-4 mr-2" /> {signature ? "Redo signature" : "Add signature"}</Button>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <label className="text-sm font-bold text-[#0f2847]">Notes</label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Delivery notes…" className="mt-2 rounded-xl border-slate-200 resize-none" data-testid="stop-notes" />
        </div>
      </section>

      {/* Sticky complete */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-20">
        <div className="container-518 max-w-xl">
          <Button onClick={complete} disabled={saving || !canComplete} data-testid="complete-delivery"
            className="w-full h-14 rounded-full bg-[#f59321] hover:bg-[#e07f10] text-white text-base font-extrabold disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Complete delivery</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
